import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type {
  SyncStatus,
  SyncType,
} from '@/features/chronograph-dashboard/types'
import { useSyncHistory } from '@/hooks/use-chronograph'
import {
  formatDateTime,
  formatSyncTypeLabel,
  ImpactCell,
  StatusBadge,
  toShortId,
} from '@/components/custom/chronograph/shared'

interface RunHistoryTabProps {
  onSelectRun: (runId: string) => void
}

const TABLE_HEAD_CLASS =
  'px-4 text-[11px] font-semibold uppercase text-primary/70 bg-primary/[0.03]'

const SKELETON_ROWS = Array.from({ length: 5 }).map((_, i) => (
  <TableRow key={i}>
    {Array.from({ length: 9 }).map((__, j) => (
      <TableCell key={j} className="px-4 py-3">
        <Skeleton className="h-4 w-full" />
      </TableCell>
    ))}
  </TableRow>
))

export function RunHistoryTab({ onSelectRun }: RunHistoryTabProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<SyncStatus | 'ALL'>('ALL')
  const [typeFilter, setTypeFilter] = useState<SyncType | 'ALL'>('ALL')

  const historyQuery = useMemo(
    () => ({
      ...(statusFilter !== 'ALL' && { status: statusFilter }),
      ...(typeFilter !== 'ALL' && { type: typeFilter }),
      limit: 50,
    }),
    [statusFilter, typeFilter]
  )
  const history = useSyncHistory(historyQuery)

  const filteredRuns = useMemo(() => {
    const runs = history.data?.data ?? []
    const query = searchQuery.trim().toLowerCase()
    if (!query) return runs
    return runs.filter((run) => {
      const haystack = [
        run.id,
        run.type,
        run.trigger,
        run.status,
        run.user?.name ?? '',
        run.user?.email ?? '',
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [searchQuery, history.data?.data])

  const totalRuns = history.data?.data?.length ?? 0
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    statusFilter !== 'ALL' ||
    typeFilter !== 'ALL'

  return (
    <section className="overflow-hidden rounded-xl border border-primary/15 bg-background shadow-none">
      {/* Filter Header */}
      <div className="flex flex-col gap-3 border-b border-primary/10 bg-primary/[0.02] px-4 py-4 lg:px-5">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Typography variant="section-title" className="text-primary">
              Run History
            </Typography>
            <Typography variant="body-sm" className="mt-1 text-primary/70">
              Search recent sync executions, filter by type or status, and open
              a run for details.
            </Typography>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="border-primary/15 bg-background font-inter tabular-nums"
            >
              {filteredRuns.length} shown
            </Badge>
            <Typography variant="body-sm" className="text-primary/70">
              {hasActiveFilters
                ? `Filtered from ${totalRuns} recent runs`
                : `Showing the latest ${totalRuns} runs`}
            </Typography>
          </div>
        </div>

        <div className="rounded-xl border border-primary/10 bg-background p-3">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-primary/70" />
              <Input
                aria-label="Search sync runs"
                placeholder="Search run ID, user, or status..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-10 border-primary/15 bg-background pl-9 shadow-none"
              />
            </div>
            <Select
              value={typeFilter}
              onValueChange={(value) =>
                setTypeFilter(value as SyncType | 'ALL')
              }
            >
              <SelectTrigger
                aria-label="Sync type"
                className="h-10 border-primary/15 bg-background"
              >
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Types</SelectItem>
                <SelectItem value="FULL">Full Sync</SelectItem>
                <SelectItem value="METRICS">Metrics</SelectItem>
                <SelectItem value="COMPANIES">Companies</SelectItem>
                <SelectItem value="INVESTMENTS">Investments</SelectItem>
                <SelectItem value="BACKFILL">Historical Backfill</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                setStatusFilter(value as SyncStatus | 'ALL')
              }
            >
              <SelectTrigger
                aria-label="Sync status"
                className="h-10 border-primary/15 bg-background"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="RUNNING">Running</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden bg-background">
        <div className="max-h-[460px] overflow-auto overscroll-contain">
          <Table className="table-auto [&_td:first-child]:border-l-0 [&_td:last-child]:border-r-0 [&_td]:border-b-primary/[0.1] [&_td]:border-r-primary/[0.05] [&_tbody_tr:last-child_td]:border-b-0 [&_th:first-child]:border-l-0 [&_th:last-child]:border-r-0 [&_th]:border-b-primary/[0.14] [&_th]:border-r-primary/[0.06]">
            <TableHeader className="sticky top-0 z-10">
              <TableRow className="hover:bg-transparent">
                <TableHead className={cn(TABLE_HEAD_CLASS, 'w-[124px]')}>
                  Run
                </TableHead>
                <TableHead className={cn(TABLE_HEAD_CLASS, 'w-[132px]')}>
                  Type
                </TableHead>
                <TableHead className={cn(TABLE_HEAD_CLASS, 'w-[140px]')}>
                  Status
                </TableHead>
                <TableHead className={cn(TABLE_HEAD_CLASS, 'w-[170px]')}>
                  Impact
                </TableHead>
                <TableHead className={cn(TABLE_HEAD_CLASS, 'w-[220px]')}>
                  Started At
                </TableHead>
                <TableHead
                  className={cn(TABLE_HEAD_CLASS, 'w-[110px] text-right')}
                >
                  Duration
                </TableHead>
                <TableHead
                  className={cn(TABLE_HEAD_CLASS, 'w-[110px] text-right')}
                >
                  Skipped
                </TableHead>
                <TableHead className={cn(TABLE_HEAD_CLASS, 'w-[170px]')}>
                  User
                </TableHead>
                <TableHead
                  className={cn(TABLE_HEAD_CLASS, 'w-[96px] text-right')}
                >
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.isError ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Typography
                        variant="body-sm"
                        className="text-destructive"
                      >
                        Failed to load sync history.
                      </Typography>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => history.refetch()}
                      >
                        Retry
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : history.isLoading ? (
                SKELETON_ROWS
              ) : filteredRuns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center">
                    <Typography variant="body-sm" className="text-primary/70">
                      No runs match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRuns.map((run, index) => (
                  <TableRow
                    key={run.id}
                    className={cn(
                      'cursor-pointer hover:bg-primary/[0.045] [&_td]:cursor-pointer',
                      index % 2 === 1 && 'bg-primary/[0.01]'
                    )}
                    tabIndex={0}
                    role="button"
                    onClick={() => onSelectRun(run.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelectRun(run.id)
                      }
                    }}
                  >
                    <TableCell className="px-4 py-3 font-mono text-[13px]">
                      {toShortId(run.id)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-[13px]">
                      {formatSyncTypeLabel(run.type)}
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <StatusBadge status={run.status} />
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <ImpactCell run={run} />
                    </TableCell>
                    <TableCell className="px-4 py-3 text-[13px]">
                      {formatDateTime(run.startedAt)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right font-inter tabular-nums text-[13px]">
                      {run.duration ?? '\u2014'}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right font-inter tabular-nums text-[13px]">
                      {run.skipped}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-[13px]">
                      {run.user?.name ?? 'System'}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <Button
                        variant="link"
                        className="h-auto p-0 text-xs"
                        aria-label={`Open details for run ${toShortId(run.id)}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          onSelectRun(run.id)
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </section>
  )
}
